import { useState, useEffect } from 'react';
import { api, handleAuthFailure } from '../data/api';
import Badge from '../components/Badge';
import StatCard from '../components/StatCard';
import { useTheme } from '../context/ThemeContext';

function fmt(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ts; }
}

function HealthBar({ label, value, color }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)' }}>{value != null ? `${value}%` : '—'}</span>
      </div>
      <div style={{ height: 4, borderRadius: 2, background: 'var(--border)' }}>
        <div style={{ height: '100%', borderRadius: 2, width: `${Math.min(value || 0, 100)}%`, background: color, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function DeviceRow({ dev }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer', background: expanded ? 'var(--surface-alt)' : 'transparent' }}
      >
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ink)', fontWeight: 600, minWidth: 120 }}>{dev.id}</span>
        <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-sub)', flex: 1 }}>{dev.mac_address}</span>
        {dev.channel_name && (
          <span style={{ fontSize: 10, background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 7px', color: 'var(--text-sub)' }}>
            {dev.channel_name}
          </span>
        )}
        <Badge status={dev.status} />
        <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{fmt(dev.last_seen)}</span>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <div style={{ padding: '10px 14px 14px', background: 'var(--surface-alt)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <HealthBar label="CPU"  value={dev.cpu_usage}   color="#3b82f6" />
          <HealthBar label="RAM"  value={dev.ram_usage}   color="#8b5cf6" />
          <HealthBar label="Temp" value={dev.temperature} color="#f59e0b" />
          <HealthBar label="Disk" value={dev.disk_usage}  color="#10b981" />
          <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>
            <span style={{ color: 'var(--text-dim)' }}>IP: </span>{dev.ip_address || '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>
            <span style={{ color: 'var(--text-dim)' }}>Model: </span>{dev.device_type === 'self-srt-connect' ? '' : (dev.device_model || '—')}
          </div>
        </div>
      )}
    </div>
  );
}

function ChildCard({ child, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const online   = child.devices.filter(d => d.status === 'online').length;
  const offline  = child.devices.filter(d => d.status === 'offline' && d.device_type !== 'self-srt-connect').length;
  const degraded = child.devices.filter(d => d.status === 'degraded').length;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
      {/* Card header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', cursor: 'pointer', background: 'var(--surface)', borderBottom: open ? '1px solid var(--border)' : 'none' }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)' }}>{child.org_name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'monospace', marginTop: 2 }}>{child.id}</div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{child.devices.length}</div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Devices</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#22c55e' }}>{online}</div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Online</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>{offline}</div>
            <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Offline</div>
          </div>
          {degraded > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>{degraded}</div>
              <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase' }}>Degraded</div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 200, alignItems: 'center' }}>
            {child.channels.map(ch => (
              <span key={ch.id} style={{ fontSize: 10, background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px', color: 'var(--text-sub)', whiteSpace: 'nowrap' }}>
                {ch.display_name}
              </span>
            ))}
          </div>
        </div>
        <Badge status={child.status} />
        <span style={{ fontSize: 13, color: 'var(--text-dim)', marginLeft: 8 }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Device list */}
      {open && (
        <div style={{ background: 'var(--surface)' }}>
          {child.devices.length === 0 ? (
            <div style={{ padding: '18px 14px', fontSize: 12, color: 'var(--text-dim)', textAlign: 'center' }}>No devices registered</div>
          ) : (
            child.devices.map(dev => <DeviceRow key={dev.id} dev={dev} />)
          )}
        </div>
      )}
    </div>
  );
}

export default function ParentAffiliateDashboard({ user }) {
  useTheme();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.affiliates.parentDashboard(user.affiliate_id);
      setData(res);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: 'var(--text-dim)' }}>Loading…</div>
  );
  if (error) return (
    <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: 'var(--red,#dc2626)' }}>{error}</div>
  );
  if (!data) return null;

  const filtered = (data.children || []).filter(c =>
    !search.trim() ||
    c.org_name.toLowerCase().includes(search.toLowerCase()) ||
    c.id.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>
            {data.parent?.org_name || 'Affiliate Dashboard'}
          </h1>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 3, fontFamily: 'monospace' }}>
            {user.affiliate_id} · {data.children.length} affiliate{data.children.length !== 1 ? 's' : ''}
          </div>
        </div>
        <button
          onClick={load}
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 14px', fontSize: 11, color: 'var(--text-sub)', cursor: 'pointer', fontFamily: 'inherit' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Summary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Affiliates" value={data.children.length} />
        <StatCard label="Total Devices"  value={data.summary.total} />
        <StatCard label="Online"         value={data.summary.online}   color="#22c55e" />
        <StatCard label="Offline"        value={data.summary.offline}  color="#ef4444" />
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search affiliates…"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 12px', fontSize: 12, color: 'var(--text)', fontFamily: 'inherit', outline: 'none', width: 260 }}
        />
        {search && <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--text-dim)' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>}
      </div>

      {/* affiliate cards */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, fontSize: 12, color: 'var(--text-dim)' }}>
          {search ? `No affiliates match "${search}"` : 'No affiliates found.'}
        </div>
      ) : (
        filtered.map((child, i) => (
          <ChildCard key={child.id} child={child} defaultOpen={i === 0} />
        ))
      )}
    </>
  );
}
